import { supabase } from '../lib/supabase';
import { orgStore } from '../lib/orgStore';
import { projectStore } from '../lib/projectStore';

export interface Document {
    id: string;
    user_id: string;
    org_id: string;
    title: string;
    type: string;
    /** Full body — omitted from list fetches (getDocuments) to save egress;
     *  only populated by getDocument(id). */
    content?: string;
    project_id?: string | null;
    sections?: Record<string, unknown>;
    status: 'draft' | 'published';
    created_at: string;
    updated_at: string;
}

// Columns needed for list/picker views — excludes the heavy `content`/`sections`.
const DOCUMENT_LIST_COLUMNS = 'id, user_id, org_id, title, type, project_id, status, created_at, updated_at';

class DocumentService {
    async createDocument(doc: Partial<Document>): Promise<Document> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const orgId = orgStore.getOrgId();
        if (!orgId) throw new Error('No organization found');

        const { data, error } = await supabase
            .from('documents')
            .insert({
                ...doc,
                user_id: user.id,
                org_id: orgId,
                project_id: doc.project_id ?? projectStore.getProjectId(),
                updated_at: new Date().toISOString(),
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    }

    async getDocument(id: string): Promise<Document | null> {
        const orgId = orgStore.getOrgId();
        if (!orgId) throw new Error('No organization found');

        const { data, error } = await supabase
            .from('documents')
            .select('*')
            .eq('id', id)
            .eq('org_id', orgId)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null; // not found
            throw error;
        }
        return data;
    }

    // RLS already blocks cross-org writes; the explicit org_id filter is
    // defense in depth so a policy regression cannot turn into a data leak.
    async updateDocument(id: string, updates: Partial<Document>): Promise<void> {
        const orgId = orgStore.getOrgId();
        if (!orgId) throw new Error('No organization found');

        // Never let a caller move a row to another org or reassign ownership.
        const { org_id, user_id, id: _id, ...safeUpdates } = updates;

        const { error } = await supabase
            .from('documents')
            .update({ ...safeUpdates, updated_at: new Date().toISOString() })
            .eq('id', id)
            .eq('org_id', orgId);

        if (error) throw error;
    }

    async deleteDocument(id: string): Promise<void> {
        const orgId = orgStore.getOrgId();
        if (!orgId) throw new Error('No organization found');

        const { error } = await supabase
            .from('documents')
            .delete()
            .eq('id', id)
            .eq('org_id', orgId);

        if (error) throw error;
    }

    // RLS automatically scopes this to the user's org — no filter needed
    async getDocuments(projectId?: string | null): Promise<Document[]> {
        let query = supabase
            .from('documents')
            .select(DOCUMENT_LIST_COLUMNS)
            .order('updated_at', { ascending: false });

        if (projectId) query = query.eq('project_id', projectId);

        const { data, error } = await query;
        if (error) throw error;
        return data || [];
    }
}

export const documentService = new DocumentService();
